'use client'

import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import { BreadcrumbView } from '../../../../../../context/breadcrumb.context'
import { IconAdd, IconAiDocument } from '../../../../../../shared/icon/icon'
import { useApiFormEmbedding, useApiListDocument, useEmbeddingNodeProgress } from '../../../../../../api-new/state/ai'
import { Menu, MenuDelete, MenuItem } from '../../../../../../shared/menu/menu'
import { CardFullHeightScrollY } from '../../../../../../shared/card/card-full-height-scroll-y'
import { useSelectFile } from '../../../../../../shared/file/select/file-select'
import { UploadList } from '../../../../../../shared/manager-upload/component/upload-list'
import { FileDrop } from '../../../../../../shared/file/file-drop'
import { TableAgClient } from '../../../../../../shared/table-builder/table-ag-client'
import { ColumnText, ColumnMenu, ColumnDate, ColumnDateTime, ColumnSize } from '../../../../../../shared/table-builder/table-columns'
import { useApiFormFileAI } from '../../../../../../api-new/state/storage'

const Progress = ({ progress }) => {
  const precent = useMemo(
    () => {
      let fixed = Number.parseFloat(progress).toFixed(2)
      return `${fixed}%`
    },
    [progress]
  )

  return (
    <div className="progress">
      <div className={`progress-bar`} role="progressbar" style={{ width: precent }}>
        {precent}
      </div>
    </div>
  )
}

const useUploader = onComplete => {
  const apiFormFileAI = useApiFormFileAI()

  const [uploads, updateUploads] = useState([])

  const [count, updateCount] = useState(0)

  const progress = useMemo(
    () => {
      if (!uploads.length) {
        return 0
      }

      return count / uploads.length * 100
    },
    [uploads, count]
  )

  useEffect(
    () => {
      let uploadAsync = async () => {
        for (let i = 0; i < uploads.length; i++) {
          let upload = uploads[i]
          const body = await upload.file.arrayBuffer()
          await apiFormFileAI.put(upload.asset.id, body)
          updateCount(count => {
            return (count += 1)
          })
        }

        if (uploads.length) {
          updateUploads([])
          updateCount(0)
          onComplete && onComplete()
        }
      }

      uploadAsync()
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [uploads]
  )

  const addUploads = uploads => {
    updateUploads(uploads)
    updateCount(0)
  }

  return { uploads, progress, addUploads }
}

export const AiDocumentList = ({ nodeID }) => {
  // const uploadManager = useContextUploadManager()

  const apiListDocument = useApiListDocument()

  const uploadFiles = useCallback(async files => {
    let items = Object.values(files)

    let data = items.map(file => {
      return {
        nodeID: nodeID,
        name: file.name
      }
    })

    let assets = await apiListDocument.postSync(`/${nodeID}/batch`, data)

    let uploads = []

    assets.forEach(asset => {
      let file = items.find(item => item.name === asset.name)
      uploads.push({ asset, file })
    })

    addUploads(uploads)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleMatch = () => {} //apiListDocument.get(`${nodeID}/node`)

  const selectFile = useSelectFile(files => uploadFiles(files))

  // useEffect(() => {
  //   const listener = item => apiListDocument.getID(item.asset.id)
  //   uploadManager.listenerAdd(listener)
  //   return () => uploadManager.listenerRemove(listener)
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [])

  const handleAddDocuments = () => selectFile()

  const scanRef = useRef()

  const handleScanAll = () => scanRef.current.start()

  const handleDelete = asset => apiListDocument.delete(asset.id)

  const handleScanAllComplete = () => apiListDocument.get(`${nodeID}/node`)

  ////////////////////////////////////////////////////////

  const { progress, addUploads } = useUploader(handleScanAll)

  ////////////////////////////////////////////////////////

  const HeaderMenu = () =>
    <Menu>
      <MenuItem Icon={IconAdd} text="add" onClick={handleAddDocuments} show />
      <MenuItem Icon={IconAiDocument} text="scan all" onClick={handleScanAll} show />
    </Menu>

  const handleDrop = files => uploadFiles(files)

  const tableRef = useRef()

  const handleReady = () => apiListDocument.get(`${nodeID}/node`)
  const handleDestroyed = () => apiListDocument.init()

  const apiFormEmbedding = useApiFormEmbedding()

  const handleScanAsset = async asset => {
    await apiFormEmbedding.postArg(`/${asset.id}/asset`)
    await apiListDocument.getID(asset.id)
  }

  const handleComplete = () => {
    handleReady()
  }

  return (
    <React.Fragment>
      <BreadcrumbView Icon={IconAiDocument} text="AI documents" onMatch={handleMatch}>
        <CardFullHeightScrollY title="AI documents" HeaderMenu={HeaderMenu}>
          <UploadList onComplete={handleComplete} />
          <ScanDocuments ref={scanRef} nodeID={nodeID} onComplete={handleScanAllComplete} />
          {!!progress && <Progress progress={progress} />}
          <FileDrop allowedFileTypes={['text/markdown', 'application/pdf']} onDrop={handleDrop}>
            <TableAgClient ref={tableRef} apiList={apiListDocument} hideFloatingFilter onReady={handleReady} onDestoryed={handleDestroyed}>
              <ColumnDate field="createdAt" />
              <ColumnText field="name" />
              <ColumnSize field="contentLength" header="Size" />
              <ColumnDateTime field="embeddedAt" />
              <ColumnMenu
                menu={row =>
                  <Menu small>
                    <MenuDelete show onClick={() => handleDelete(row)} />
                    <MenuItem Icon={IconAiDocument} text="Scan" show onClick={() => handleScanAsset(row)} />
                  </Menu>}
              />
            </TableAgClient>
          </FileDrop>
        </CardFullHeightScrollY>
      </BreadcrumbView>
    </React.Fragment>
  )
}

const ScanDocuments = forwardRef(({ nodeID, onComplete }, ref) => {
  const [progress, updateProgress] = useState(null)
  const handleProgress = progress => updateProgress(progress)

  const handleComplete = () => {
    updateProgress(null)
    onComplete && onComplete()
  }

  const [scanStart] = useEmbeddingNodeProgress(handleProgress, handleComplete)

  useImperativeHandle(
    ref,
    () => ({
      start: () => scanStart({ nodeID })
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  const hold = useMemo(
    () => {
      if (!progress) {
        return 0
      }

      if (!progress.progressLength) {
        return 0
      }

      return progress.progressCount / progress.progressLength * 100
    },
    [progress]
  )

  return (
    <React.Fragment>
      {progress &&
        <div>
          <Progress progress={hold} />
        </div>}
    </React.Fragment>
  )
})
